/* Program Name: BasicGrid.js
 **  Description: several pre-styled MUI buttons that will be commonly used
 **
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-03-17  STT  Initial program
 ** SD6391     2024-07-23  sgs  SD-6762 - Added location param
 ** SD6391     2025-01-22  SGS  SD8794 - Replaced props.location with useHistory
 *********************************************************************************/

/** React imports */
import React, { useState, useEffect } from 'react'

/**MUI imports */
import { Tooltip, Box, Typography } from '@mui/material'
import { Save, Add, Close, Refresh } from '@mui/icons-material'
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro'

/**React - Router imports */
import { NavLink } from 'react-router-dom'

/**Custom Component imports */
import {
  ErrorButton,
  SuccessButton,
  SquarePrimaryButton,
} from './ActionColorButtons'
import { FlexBox, FlexOne } from '../Common/CustomDivs'

/**API imports */
import { search } from '../../API/search'

/**Basic Grid ***************************************************************************
 *  @param {object}      gridData     > array that holds all of the table data
 *  @param {function}    save         > function that contains logic for the "Save" button
 *  @param {object}      columns      > array that holds all of the column information for the grid
 *  @param {String}      addLink      > String that holds the path to add a new obj to the grid
 *  @param {String}      updateLink   > String that holds the path to update the selected obj
 *  @param {function}    del          > function that has the delete API for objs on the grid
 *  @param {Boolean}     create       > Boolean that determines wether or not the adding functionality is there
 *  @param {Boolean}     dataExport   > Boolean that determines wether or not the export functionality is there
 *  @param {Boolean}     inline       > Boolean that determines wether or not the inline functionality is there
 *  @param {JSX element} modal        > JSX element that contains the modal for editing/adding items to the grid
 *  @param {String}      gridTitle    > String that will be the title of the grid
 *  @param {Boolean}     refresh      > Boolean that determines wether or not the refresh functionality is there
 *  @param {JSX element} extraButtons > JSX element that contains any extra buttons to be added to the toolbar of the grid
 *  @param {Integer}     pageSize     > Integer that determines the amount of items on each page of the grid
 *  @param {Boolean}     searchable   > Boolean that determines wether the grid will have a search function
 */
const BasicGrid = ({
  gridData,
  save,
  addLink,
  location,
  create,
  inline,
  gridTitle,
  refresh,
  extraButtons,
  realColumns,
  rowStyles,
  styleCheck,
  onSelect,
  selectionModel,
  pageSize,
  isGridLoading,
  rowHeight,
  gridSX,
  initState,
}) => {
  /****************************************************************************/
  /**Local Variables */

  //API used to access the XGrid internal data //DOCS: https://material-ui.com/components/data-grid/editing/ **Half Way down page** 6.16.21
  //const apiRef = useGridApiRef()

  //holds the serach values currently in the search input
  const [searchTerm, setSearchTerm] = useState('')

  //holds the array of results filtered by the search term
  const [searchResults, setSearchResults] = useState([...gridData])

  //holds the state of if the grid was editted inline or not
  const [editted, setEditted] = useState(false)

  const [pagesize, setPageSize] = useState(pageSize ? pageSize : 100)

  //holds any editted data that was not saved to the db yet
  const [unsavedData, setUnsavedData] = useState([...gridData])

  /****************************************************************************/
  /**Local Functions */

  /** getCurrentGridData returns the data currently within the grid */
  // const getCurrentGridData = () =>{
  //   let map = apiRef.current.getRowModels()
  //   return Array.from(map).map(([id, value]) => ({id, ...value}))
  // }

  /** handleSave
   *
   *  handles functionality for saving
   */
  const handleSave = () => {
    save(unsavedData)
  }

  /** handleCancel
   *
   * handles canceling the edits on the grid
   */
  const handleCancel = () => {
    setEditted(false)
    setSearchResults([...gridData])
  }

  /** handleRefresh
   *
   *  handles functionality for refreshing the data in the grid
   */
  const handleRefresh = () => {
    refresh()
  }

  /** Handle Edit Cell Change Committed
   * handles storing any committed changes to the inline grid edits
   * stores the new grid data in the unsavedData state to be saved to db
   * later
   */
  const handleEditCellChangeCommitted = (params) => {
    var row = unsavedData.find((item) => item.id === params.id)
    row[params.field] = params.props.value
    setUnsavedData([...unsavedData])
  }

  /**
   * Handle Edit Cell Change
   * toggles the edit state to true
   */
  const handleEditCellChange = () => {
    setEditted(true)
  }

  /****************************************************************************/
  /**UseEffect */

  //this useEffect will monitor the searchTerm input and generate a new list of data based on the searchTerm
  //the function search() will require the original list of data, the searchTerm and an array of columns that will be searched
  useEffect(() => {
    if (searchTerm === null && !editted) {
      setSearchResults([...gridData])
    } else if (searchTerm === null && editted) {
      setSearchResults([...unsavedData])
    } else if (editted) {
      setSearchResults(search(searchTerm, unsavedData, realColumns))
    } else {
      setSearchResults(search(searchTerm, gridData, realColumns))
    }
  }, [searchTerm, editted, realColumns, gridData, unsavedData])

  useEffect(() => {
    setSearchResults([...gridData])
  }, [gridData])

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarQuickFilter />
        <GridToolbarExport />
      </GridToolbarContainer>
    )
  }

  /****************************************************************************/
  /**Render*/

  return (
    <>
      <div style={{}}>
        <FlexBox>
          {gridTitle ? <Typography variant="h3">{gridTitle}</Typography> : null}
          <FlexOne />
          {extraButtons && extraButtons()}
          {addLink && (
            <Tooltip disableInteractive title="Add">
              <SuccessButton
                variant="contained"
                component={NavLink}
                to={{ pathname: addLink }}
              >
                <Add fontSize={'small'} />
                &nbsp;Add
              </SuccessButton>
            </Tooltip>
          )}
          {create && (
            <Tooltip disableInteractive title="Add">
              <SuccessButton variant="contained" onClick={create}>
                <Add fontSize={'small'} />
                &nbsp;Add
              </SuccessButton>
            </Tooltip>
          )}
          {inline && (
            <Tooltip disableInteractive title="Save Changes">
              <div>
                <SuccessButton
                  variant="contained"
                  disabled={!editted}
                  onClick={handleSave}
                >
                  <Save fontSize={'small'} />
                  &nbsp;Save
                </SuccessButton>
              </div>
            </Tooltip>
          )}
          {inline && (
            <Tooltip disableInteractive title="Cancel Unsaved Changes">
              <div>
                <ErrorButton
                  variant="contained"
                  disabled={!editted}
                  onClick={handleCancel}
                >
                  <Close fontSize={'small'} />
                  &nbsp;Cancel
                </ErrorButton>
              </div>
            </Tooltip>
          )}
          {refresh && (
            <Tooltip disableInteractive title="Refresh Grid Data">
              <div>
                <SquarePrimaryButton
                  variant="contained"
                  onClick={handleRefresh}
                >
                  <Refresh fontSize={'small'} />
                </SquarePrimaryButton>
              </div>
            </Tooltip>
          )}
        </FlexBox>
      </div>
      <Box
        sx={{
          height: '80vh',
          width: '100%',
          marigin: 32,
          backgroundColor: '#FFFFFF70',
          ...rowStyles,
        }}
      >
        <DataGridPro
          // apiRef={apiRef}
          {...{ rows: [...searchResults], columns: [...realColumns] }}
          sx={{
            borderRadius: 0,
            border: '.5px solid #aaa',
            boxShadow: 'none',
            ...gridSX,
          }}
          initialState={initState}
          loading={isGridLoading}
          rowHeight={rowHeight ? rowHeight : 42}
          pagination
          pageSize={pagesize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[100, 200, 500]}
          selectionModel={selectionModel ? selectionModel : []}
          checkboxSelection={onSelect ? true : false}
          onSelectionModelChange={
            onSelect
              ? (selectModel) => {
                  onSelect(selectModel)
                }
              : () => {}
          }
          disableSelectionOnClick={onSelect ? false : true}
          onEditCellChangeCommitted={handleEditCellChangeCommitted}
          onEditCellChange={handleEditCellChange}
          columnBuffer={realColumns.length}
          getRowClassName={(params) => {
            if (styleCheck) {
              return styleCheck(params.row)
            }
          }}
          components={{
            Toolbar: CustomToolbar,
          }}
        />
      </Box>
    </>
  )
}

export default BasicGrid
